import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Menu, Bell, Search, Heart, Wallet, ArrowUpRight, ArrowDownRight, CheckCircle2, X } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { formatCurrency, formatRelativeTime } from '../../utils/formatters';
type TopBarProps = {
  onMenuClick: () => void;
};
export const TopBar: React.FC<TopBarProps> = ({
  onMenuClick
}) => {
  const {
    state
  } = useAppContext();
  const {
    user,
    transactions,
    donations,
    campaigns
  } = state;
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [readCount, setReadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Build notifications from real data
  const notifications = useMemo(() => {
    const items: Array<{
      id: string;
      icon: React.ReactNode;
      title: string;
      description: string;
      time: string;
      color: string;
    }> = [];
    // Recent donations received on user's campaigns
    const myCampaignIds = campaigns.filter(c => c.userId === user?.id).map(c => c.id);
    donations.filter(d => myCampaignIds.includes(d.campaignId)).slice(0, 3).forEach(d => {
      const camp = campaigns.find(c => c.id === d.campaignId);
      items.push({
        id: `don-${d.id}`,
        icon: <Heart size={16} className="text-pink-500" data-id="element-284" />,
        title: 'New Donation Received',
        description: `${d.donorName} donated ${formatCurrency(d.amount)} to "${camp?.title || 'your campaign'}"`,
        time: d.createdAt,
        color: 'bg-pink-50'
      });
    });
    // Recent transactions
    transactions.slice(0, 4).forEach(tx => {
      const isIncoming = tx.amount > 0;
      items.push({
        id: `tx-${tx.id}`,
        icon: isIncoming ? <ArrowDownRight size={16} className="text-green-500" data-id="element-285" /> : <ArrowUpRight size={16} className="text-gray-500" data-id="element-286" />,
        title: tx.title,
        description: `${isIncoming ? '+' : ''}${formatCurrency(tx.amount)}`,
        time: tx.date,
        color: isIncoming ? 'bg-green-50' : 'bg-gray-50'
      });
    });
    // Campaign milestones
    campaigns.filter(c => c.userId === user?.id && c.raisedAmount >= c.targetAmount * 0.5).slice(0, 2).forEach(c => {
      const pct = Math.min(Math.round(c.raisedAmount / c.targetAmount * 100), 100);
      items.push({
        id: `mile-${c.id}`,
        icon: <CheckCircle2 size={16} className="text-brand-primary" data-id="element-287" />,
        title: 'Campaign Milestone',
        description: `"${c.title}" has reached ${pct}% of its goal!`,
        time: c.createdAt,
        color: 'bg-green-50'
      });
    });
    // Sort by time descending
    items.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
    return items.slice(0, 8);
  }, [transactions, donations, campaigns, user]);
  const unreadCount = Math.max(0, notifications.length - readCount);
  const handleOpen = () => {
    setIsNotifOpen(!isNotifOpen);
  };
  const handleMarkAllRead = () => {
    setReadCount(notifications.length);
    setIsNotifOpen(false);
  };
  return <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-30" data-id="element-288">
      <div className="flex items-center gap-4" data-id="element-289">
        <button onClick={onMenuClick} className="lg:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-50" data-id="element-290">
          <Menu size={24} data-id="element-291" />
        </button>
        <h1 className="text-xl font-semibold text-brand-text-dark hidden sm:block" data-id="element-292">
          Welcome back, {user?.name.split(' ')[0]}
        </h1>
      </div>

      <div className="flex items-center gap-4" data-id="element-293">
        {/* Search */}
        <div className="hidden md:flex relative" data-id="element-294">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-295" />
          <input type="text" placeholder="Search transactions..." className="pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary w-64 transition-all" data-id="element-296" />
        </div>

        {/* Notifications */}
        <div className="relative" ref={dropdownRef} data-id="element-297">
          <button onClick={handleOpen} className="relative p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-50 transition-colors" aria-label="Notifications" data-id="element-298">
            <Bell size={20} data-id="element-299" />
            {unreadCount > 0 && <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-brand-error text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-white" data-id="element-300">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>}
          </button>

          {isNotifOpen && <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-id="element-301">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-302">
                <h3 className="font-bold text-brand-text-dark" data-id="element-303">
                  Notifications
                </h3>
                <div className="flex items-center gap-2" data-id="element-304">
                  {unreadCount > 0 && <button onClick={handleMarkAllRead} className="text-xs font-medium text-brand-primary hover:text-brand-primary-dark transition-colors" data-id="element-305">
                      Mark all read
                    </button>}
                  <button onClick={() => setIsNotifOpen(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-306">
                    <X size={16} data-id="element-307" />
                  </button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto" data-id="element-308">
                {notifications.length === 0 ? <div className="py-12 text-center" data-id="element-309">
                    <Bell size={32} className="mx-auto text-gray-300 mb-3" data-id="element-310" />
                    <p className="text-sm text-brand-text-light" data-id="element-311">
                      No notifications yet
                    </p>
                  </div> : notifications.map((notif, idx) => <div key={notif.id} className={`px-5 py-3.5 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${idx >= readCount ? 'bg-brand-primary/[0.03]' : ''}`} data-id="element-312">
                      <div className={`w-9 h-9 rounded-full ${notif.color} flex items-center justify-center shrink-0 mt-0.5`} data-id="element-313">
                        {notif.icon}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-314">
                        <p className="text-sm font-semibold text-brand-text-dark leading-tight" data-id="element-315">
                          {notif.title}
                        </p>
                        <p className="text-xs text-brand-text-light mt-0.5 line-clamp-2" data-id="element-316">
                          {notif.description}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-1" data-id="element-317">
                          {formatRelativeTime(notif.time)}
                        </p>
                      </div>
                      {idx >= readCount && <div className="w-2 h-2 bg-brand-primary rounded-full shrink-0 mt-2" data-id="element-318"></div>}
                    </div>)}
              </div>
            </div>}
        </div>
      </div>
    </header>;
};